import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useCallback, useMemo, useState } from 'react';

import { type UserType, mutateUserInvite } from '@/api/user';
import { Button } from '@/components/buttons';
import {
  InstantSearchDataTable,
  type InstantSearchDataTableColumnDef,
} from '@/components/InstantSearch';
import LetteredAvatar from '@/components/LetteredAvatar';
import { Badge, Link } from '@/components/typography';
import { useNotification } from '@/providers/Notification';
import { useMe } from '@/providers/User';
import { ROUTES } from '@/router';

type Props = {
  data?: UserType[];
  totalResults?: number;
  isLoading?: boolean;
  error: Error | null;
};

const UsersListTable = ({ data, totalResults, isLoading, error }: Props) => {
  const user = useMe();
  const { pushNotification, removeNotification } = useNotification();
  const [inviteUserPendingId, setInviteUserPendingId] = useState<string | undefined>();

  const sortFields = ROUTES.users.searchParams['u.sortBy'].enumValues;

  const handleResendInvite = useCallback(
    async (user: UserType) => {
      try {
        setInviteUserPendingId(user.id);
        const { success } = await mutateUserInvite({
          name: user.name,
          email: user.email,
          role: user.role!,
        });

        if (!success) {
          throw new Error();
        }

        removeNotification({ type: 'error' });
        pushNotification({
          type: 'success',
          message: t`You have successfully resent the invitation`,
        });
      } catch {
        pushNotification({
          type: 'error',
          message: t`Apologies, but something went wrong. Please try to resend the invite again.`,
        });
      } finally {
        setInviteUserPendingId(undefined);
      }
    },
    [pushNotification, removeNotification],
  );

  const columns = useMemo<InstantSearchDataTableColumnDef<UserType>[]>(
    () => [
      {
        header: t`User Name`,
        id: 'name',
        accessorKey: 'name',
        enableSorting: sortFields.includes('name'),
        minSize: 160,
        flex: 1,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.name;
          const userIsMe = user?.id === field.id;

          return (
            <div className="flex items-center gap-2">
              <LetteredAvatar className="-my-1" name={text} shape="circle" size="medium" />
              <Link variant="caption1" to={ROUTES.userById(field.id)}>
                {text}
              </Link>
              {userIsMe && <Trans>(you)</Trans>}
            </div>
          );
        },
      },
      {
        header: t`User Email`,
        id: 'email',
        accessorKey: 'email',
        enableSorting: sortFields.includes('email'),
        minSize: 160,
        flex: 1,
        cell: ({ row }) => row.original.email,
      },
      {
        header: t`Status`,
        id: 'status',
        accessorKey: 'status',
        minSize: 200,
        flex: 1,
        enableSorting: sortFields.includes('status'),
        cell: ({ row }) => {
          const field = row.original;
          const text = field.status;
          return text === 'active' ? (
            <Badge color="success" showTooltip={false}>
              <Trans>Active</Trans>
            </Badge>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <Badge color="critical" showTooltip={false}>
                <Trans>Pending</Trans>
              </Badge>
              <Button
                className="-my-2"
                size="small"
                disabled={!!inviteUserPendingId && inviteUserPendingId !== field.id}
                isLoading={inviteUserPendingId === field.id}
                onClick={() => {
                  handleResendInvite(field);
                }}
              >
                <Trans>Resend Invite</Trans>
              </Button>
            </div>
          );
        },
      },
      {
        header: t`User Role`,
        id: 'role',
        accessorKey: 'role',
        size: 120,
        enableSorting: sortFields.includes('role'),
        cell: ({ row }) => (row.original.role === 'admin' ? t`Admin` : t`User`),
      },
    ],
    [inviteUserPendingId, handleResendInvite, user?.id, sortFields],
  );

  return (
    <InstantSearchDataTable<UserType>
      columns={columns}
      data={data}
      totalResults={totalResults}
      loading={isLoading}
      emptyMessage={<Trans>No users found.</Trans>}
      error={error}
      getRowId={(record) => record.id}
      initialColumnPinning={{
        start: ['name'],
      }}
    />
  );
};

export default UsersListTable;
